import { Container, Grid } from '@material-ui/core';
import { Role } from '@nemuru-eng/js-api-client';
import { useAuth } from '@nemuru-eng/providers';
import { useNavigate } from 'react-router-dom';

import { ActiveProfileSection } from '@/components/modules/AccountDetail/components/ActiveProfileSection';
import { DarkModeToggle } from '@/components/modules/AccountDetail/components/DarkModeToggle';
import { getNameAndIcon, Organization } from '@/components/modules/AccountDetail/components/Organization';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { LanguageSelector } from '@/components/shared/components/LanguageSelector/LanguageSelector';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { StatusTagType } from '@/config/types';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useScreen } from '@/hooks/useScreen';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const AccountDetail = () => {
  const { userEntity } = useUserContext();

  const { logout, requireChangePassword } = useAuth();

  const { sm } = useScreen();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    //window.location.reload();
  };

  if (!userEntity) {
    return null;
  }

  const { name } = getNameAndIcon({ userType: userEntity.user.userType, userEntity });

  const userType = userEntity.user.userType;
  const isTelemarketingAgent = userEntity?.hasRole([Role.ROLE_IBC_TLMK_AGENT]);
  const isTelemarketingAgentOrChannel = userEntity?.hasRole([Role.ROLE_IBC_TLMK_AGENT, Role.ROLE_IBC_TLMK_CHANNEL]);

  return (
    <Container style={{ padding: 0, marginLeft: 0, marginRight: 'auto', maxWidth: '800px' }}>
      <FlexColumn width={'100%'} gridGap={20}>
        <SubSectionWrapper
          title={'USER_INFO'}
          rightComponent={
            <StandardButton
              variant="text"
              size="small"
              onClick={handleLogout}
              endIcon={<Icon size={16}>exit_to_app</Icon>}
              style={{ flexShrink: 0 }}
            >
              <Text tId={'LOGOUT'} />
            </StandardButton>
          }
        >
          <CustomPaper fill="paper">
            <FlexColumn gridGap={24}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <Font variant="overline" color="textSecondary" display="block">
                    <Text tId={'CREDENTIALS_USERNAME'} />
                  </Font>
                  <Font variant="subtitle2" color="textPrimary" display="block">
                    <strong>{userEntity.user.username}</strong>
                  </Font>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Font variant="overline" color="textSecondary" display="block">
                    <Text tId={'USER_ID'} />
                  </Font>
                  <Font variant="subtitle2" color="textPrimary" display="block">
                    <strong>{userEntity.user.userId}</strong>
                  </Font>
                </Grid>
              </Grid>
              <CustomDivider width={'100%'} margin={0} />
              <FlexRow width={'100%'}>
                <Font variant="body2" color="textSecondary" display="block">
                  <Text tId={'PASSWORD'} />
                </Font>
                <StandardButton
                  size="small"
                  variant="outlined"
                  onClick={requireChangePassword}
                  startIcon={<Icon size={16}>lock</Icon>}
                  disabled={isTelemarketingAgent}
                >
                  <Text tId={sm ? 'CHANGE_PASSWORD' : 'CHANGE'} />
                </StandardButton>
              </FlexRow>
            </FlexColumn>
          </CustomPaper>
        </SubSectionWrapper>
        <ActiveProfileSection />
        <SubSectionWrapper title={'ORGANIZATION'}>
          <CustomPaper fill="paper">
            <FlexColumn width={'100%'} gridGap={24}>
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6}>
                  <Font variant="overline" color="textSecondary" display="block">
                    <Text tId={'NAME'} />
                  </Font>
                  <Font variant="subtitle2" color="textPrimary" display="block">
                    <strong>{name}</strong>
                  </Font>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Font variant="overline" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                    <Text tId={'USER_TYPE'} />
                  </Font>
                  <StatusTag labelTextId={`user_${userType}`} type={StatusTagType.INFO} />
                </Grid>
              </Grid>
              {userEntity.isRoleMerchantChannelOrAgent() && (
                <>
                  <CustomDivider width={'100%'} margin={0} />
                  <FlexColumn width={'100%'} gridGap={16} pb={2}>
                    <Font variant="body2" color="textSecondary" display="block">
                      <Text tId={'ORGANIZATION_STRUCTURE'} />
                    </Font>
                    <Organization userEntity={userEntity} />
                  </FlexColumn>
                </>
              )}
            </FlexColumn>
          </CustomPaper>
        </SubSectionWrapper>
        <SubSectionWrapper title={'APPLICATION_LANGUAGE'}>
          <CustomPaper fill="paper">
            <LanguageSelector />
          </CustomPaper>
        </SubSectionWrapper>
        {!isTelemarketingAgentOrChannel && ( // TODO: When we propagate the dark mode setting to the embedded checkout we would remove this condition
          <SubSectionWrapper title={'THEME'}>
            <CustomPaper fill="paper">
              <DarkModeToggle />
            </CustomPaper>
          </SubSectionWrapper>
        )}
      </FlexColumn>
    </Container>
  );
};

export const AccountDetailModule = withPermissionsModule(AccountDetail);
